'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Loader2, MapPin, Pencil, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TextField } from '@/components/ui/form-fields';
import { logger } from '@/lib/utils/logger';
import { cn } from '@/lib/utils';
import {
  fetchAddressDetails,
  useAddressAutocomplete
} from './use-address-search';
import {
  hasAddressValue,
  mapAddressDetailsToStructured,
  NEW_STRUCTURED_ADDRESS
} from './address-search.utils';
import { CountryField } from './country-field';
import { isAustralianAddress } from '@/lib/schemas/inventory-form-schemas';
import { DEFAULT_COUNTRY } from '@/lib/constants/countries';
import type { AddressSuggestion, StructuredAddress } from './types';

export interface AddressSearchProps {
  /** Controlled structured address value. */
  value: StructuredAddress;
  /** Emits the full structured address on selection or manual edit. */
  onChange: (value: StructuredAddress) => void;
  label?: string;
  description?: string;
  required?: boolean;
  /** Field-level error shown beneath the control. */
  error?: string;
  placeholder?: string;
  /**
   * **How the manual-entry fields below the search box are laid out**
   * (LF-6359 / [ATTR-9] §3).
   *
   * · `'default'` — Street address full width, then Suburb / State / Postcode
   *   three across, then Country. What every shipped host renders, and the
   *   default so none of them move.
   * · `'pairs'` — Street address full width, then Suburb + State and
   *   Postcode + Country two to a line. The Contacts drawer's address popover,
   *   which is ~448px rather than a full form column: three fields across that
   *   width clipped `Chippendale` to `Chippenda`, and a pair does not.
   *
   * Street address keeps its own line in **both**, because it is the one part
   * routinely long enough that half a popover would only ellipsise it.
   *
   * Widened here rather than forked into a second address control, per
   * `frontend/CLAUDE.md` § *"If it looks like a standard, it IS the standard"* —
   * a look-alike would be a second copy of the lookup, the manual-entry reveal
   * and the country picker.
   */
  fieldLayout?: 'default' | 'pairs';
  /**
   * **The states an Australian address may name, as a picker** (VALE-1940).
   *
   * Supplied, the State part is a closed choice while the country is Australia
   * and free text again for an overseas region (LF-4549). The inventory address
   * rows pass the state codes, because the AI field registry allows exactly
   * those, and a typed "Victoria" and an extracted one must not be treated
   * differently. Omitted, every other host renders byte-for-byte as before.
   *
   * A native `select`: this renders inside the attribute editor's popover, and
   * a portalled listbox there is an outside click that dismisses the popover.
   */
  stateOptions?: readonly { value: string; label: string }[];
  /**
   * **The chrome the manual-entry parts wear, when the host is not a page
   * form** (VALE-863).
   *
   * Widened here for the same reason `fieldLayout` was, and recorded in the same
   * terms: the alternative is a second address control, and a look-alike would
   * be a second copy of the lookup, the manual-entry reveal and the country
   * picker (`frontend/CLAUDE.md` § *"If it looks like a standard, it IS the
   * standard"*).
   *
   * Omitted — every shipped host — the parts keep their page-form geometry and
   * this component renders byte-for-byte as before. Supplied, they take the
   * caller's classes and the block's own vertical rhythm tightens to match:
   * `space-y-4` / `gap-4` between five parts is page-form spacing, and inside a
   * ~448px popover it is what made the address editor read *"looser and taller
   * than the popovers it sits alongside"* in Tom's 27 Aug review.
   *
   * **The classes are the caller's, not ours.** They are the attribute
   * treatment's `ATTRIBUTE_POPOVER_PART*` tokens, and that package is the layer
   * above this one — naming them here would be the second, drifting definition.
   * `attribute-address-editor.tsx` is the one caller and already owns them.
   */
  partChrome?: {
    /** The column the parts stack in — replaces the block's `space-y-4`. */
    parts: string;
    /** One part's label-over-control wrapper — replaces `TextField`'s `space-y-2`. */
    part: string;
    /** A part's label. */
    label: string;
    /** A part's input, or the country picker's trigger. */
    control: string;
    /**
     * **One row of the autocomplete list, and the manual-entry escape below it**
     * (VALE-863).
     *
     * The list is what a reader looks at while typing, and at page-form `text-sm`
     * inside a 13px popover it was the loosest thing in the panel — the same
     * "looser and taller than the popovers it sits alongside" the parts had.
     * Scoped through `partChrome` like the rest, so the page-form hosts
     * (`DeceasedForm`, `new-enquiry-dialog`, `edit-matter-dialog`,
     * `AddressSection`) keep their own geometry untouched.
     */
    suggestion: string;
    /** The description and error lines that bracket the block. */
    hint: string;
  };
}

/**
 * Shared internal address-search field. Autocompletes against the internal
 * Google Places proxy and, on selection, resolves the pick to structured
 * fields (`streetAddress`, `suburb`, `state`, `postcode`, `country`). A
 * manual-entry fallback writes the same structured fields for addresses the
 * search can't resolve. Reusable across the property, contact, and
 * matter/estate surfaces.
 */
export function AddressSearch({
  value,
  onChange,
  label,
  description,
  required,
  error,
  placeholder = 'Start typing an address...',
  fieldLayout = 'default',
  stateOptions,
  partChrome
}: AddressSearchProps) {
  const id = useId();
  const listboxId = `${id}-listbox`;
  const errorId = `${id}-error`;

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isResolving, setIsResolving] = useState(false);
  // Reveal the structured fields once an address exists or manual entry begins.
  const [showFields, setShowFields] = useState(() => hasAddressValue(value));

  const containerRef = useRef<HTMLDivElement>(null);

  const { suggestions, isLoading } = useAddressAutocomplete(query, true);

  // Close the dropdown on outside click.
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const revealManualEntry = useCallback(() => {
    setShowFields(true);
    setIsOpen(false);
    setActiveIndex(-1);
    setQuery('');
    // Starting a brand-new address: seed the default country so it is set (and saved)
    // without the user having to pick it. An address already carrying data is left alone.
    if (!hasAddressValue(value)) {
      onChange(NEW_STRUCTURED_ADDRESS);
    }
    setTimeout(() => document.getElementById(`${id}-street`)?.focus(), 0);
  }, [id, value, onChange]);

  const handleSelect = useCallback(
    async (suggestion: AddressSuggestion) => {
      setIsOpen(false);
      setActiveIndex(-1);
      setQuery('');
      setIsResolving(true);
      try {
        const details = await fetchAddressDetails(suggestion.placeId);
        onChange(mapAddressDetailsToStructured(details));
      } catch (err: unknown) {
        // Couldn't resolve details — fall back to the raw description in the
        // street field and let the user complete the rest manually.
        logger.warn('[AddressSearch] details lookup failed', err);
        onChange({
          ...NEW_STRUCTURED_ADDRESS,
          streetAddress: suggestion.description
        });
      } finally {
        setIsResolving(false);
        setShowFields(true);
      }
    },
    [onChange]
  );

  const updateField = (field: keyof StructuredAddress, fieldValue: string) => {
    onChange({
      ...value,
      // The country picker shows the default jurisdiction when nothing is set, so persist
      // it alongside the first edit — otherwise what the user sees selected is not what
      // gets saved.
      country: value.country.trim() || DEFAULT_COUNTRY,
      // A manual edit invalidates the provider's formatted string.
      formattedAddress: undefined,
      [field]: fieldValue
    });
  };

  const optionCount = suggestions.length + 1; // +1 for the manual-entry row

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % optionCount);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? optionCount - 1 : prev - 1));
    } else if (event.key === 'Enter') {
      if (activeIndex < 0) return;
      event.preventDefault();
      if (activeIndex < suggestions.length) {
        void handleSelect(suggestions[activeIndex]);
      } else {
        revealManualEntry();
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div
      className={partChrome ? 'flex flex-col gap-2' : 'space-y-3'}
      ref={containerRef}
    >
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
        <Input
          id={id}
          type="text"
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxId : undefined}
          aria-activedescendant={
            activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined
          }
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          className="pr-3 pl-9"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(e.target.value.trim().length > 0);
            setActiveIndex(-1);
          }}
          onFocus={() => {
            if (query.trim().length > 0) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
        {(isLoading || isResolving) && (
          <Loader2 className="text-muted-foreground absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 animate-spin" />
        )}

        {isOpen && (
          <div
            id={listboxId}
            role="listbox"
            className="bg-popover absolute top-full z-20 mt-1 w-full overflow-hidden rounded-md border shadow-lg"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.placeId}
                id={`${id}-option-${index}`}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  void handleSelect(suggestion);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  'flex w-full items-start gap-2 px-3 py-2.5 text-left',
                  partChrome ? partChrome.suggestion : 'text-sm',
                  index === activeIndex ? 'bg-accent' : 'hover:bg-accent/50'
                )}
              >
                <MapPin className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{suggestion.mainText}</span>
                  {suggestion.secondaryText && (
                    <span className="text-muted-foreground ml-1">
                      {suggestion.secondaryText}
                    </span>
                  )}
                </span>
              </button>
            ))}

            <button
              id={`${id}-option-${suggestions.length}`}
              type="button"
              role="option"
              aria-selected={activeIndex === suggestions.length}
              onMouseDown={(e) => {
                e.preventDefault();
                revealManualEntry();
              }}
              onMouseEnter={() => setActiveIndex(suggestions.length)}
              className={cn(
                'flex w-full items-center gap-2 border-t px-3 py-2.5 text-left',
                partChrome ? partChrome.suggestion : 'text-sm',
                activeIndex === suggestions.length
                  ? 'bg-accent'
                  : 'hover:bg-accent/50'
              )}
            >
              <Pencil className="text-muted-foreground h-4 w-4 shrink-0" />
              <span className="text-muted-foreground">
                Enter address manually
              </span>
            </button>
          </div>
        )}
      </div>

      {description && (
        <p
          className={cn(
            'text-muted-foreground',
            partChrome ? partChrome.hint : 'text-xs'
          )}
        >
          {description}
        </p>
      )}

      {showFields && (
        <div className={partChrome ? partChrome.parts : 'space-y-4'}>
          <TextField
            id={`${id}-street`}
            label="Street Address"
            value={value.streetAddress}
            onChange={(val) => updateField('streetAddress', val)}
            required={required}
            className={partChrome?.part}
            labelClassName={partChrome?.label}
            controlClassName={partChrome?.control}
          />
          {/*
            Suburb and State pair up in both layouts; what differs is whether
            Postcode joins them on that line (`default`, three across) or pairs
            with Country on the next one (`pairs`).
          */}
          <div
            className={cn(
              'grid',
              partChrome ? 'gap-2' : 'gap-4',
              fieldLayout === 'pairs' ? 'grid-cols-2' : 'grid-cols-3'
            )}
          >
            <TextField
              id={`${id}-suburb`}
              label="Suburb"
              value={value.suburb}
              onChange={(val) => updateField('suburb', val)}
              required={required}
              className={partChrome?.part}
              labelClassName={partChrome?.label}
              controlClassName={partChrome?.control}
            />
            {stateOptions && isAustralianAddress(value.country) ? (
              <div className={cn('space-y-2', partChrome?.part)}>
                <Label htmlFor={`${id}-state`} className={partChrome?.label}>
                  State
                  {required && <span className="text-destructive ml-1">*</span>}
                </Label>
                <select
                  id={`${id}-state`}
                  value={value.state}
                  onChange={(event) => updateField('state', event.target.value)}
                  required={required}
                  className={cn(
                    'bg-muted/50 w-full rounded-md px-3 py-2 text-sm',
                    partChrome?.control
                  )}
                >
                  <option value="" />
                  {/*
                    A stored value outside the list (typed before this picker
                    existed) stays visible rather than silently reading blank.
                  */}
                  {value.state &&
                    !stateOptions.some((o) => o.value === value.state) && (
                      <option value={value.state}>{value.state}</option>
                    )}
                  {stateOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <TextField
                id={`${id}-state`}
                // An overseas address holds a province/region, not an Australian
                // state — label it so, or the field reads as AU-only and invites
                // an incorrect AU code (LF-5933).
                label={
                  isAustralianAddress(value.country)
                    ? 'State'
                    : 'State / Region'
                }
                value={value.state}
                onChange={(val) => updateField('state', val)}
                required={required}
                className={partChrome?.part}
                labelClassName={partChrome?.label}
                controlClassName={partChrome?.control}
              />
            )}
            {fieldLayout === 'default' && (
              <TextField
                id={`${id}-postcode`}
                label="Postcode"
                value={value.postcode}
                onChange={(val) => updateField('postcode', val)}
                required={required}
                className={partChrome?.part}
                labelClassName={partChrome?.label}
                controlClassName={partChrome?.control}
              />
            )}
          </div>
          {fieldLayout === 'pairs' ? (
            <div
              className={cn('grid grid-cols-2', partChrome ? 'gap-2' : 'gap-4')}
            >
              <TextField
                id={`${id}-postcode`}
                label="Postcode"
                value={value.postcode}
                onChange={(val) => updateField('postcode', val)}
                required={required}
                className={partChrome?.part}
                labelClassName={partChrome?.label}
                controlClassName={partChrome?.control}
              />
              <CountryField
                id={`${id}-country`}
                value={value.country}
                onChange={(val) => updateField('country', val)}
                className={partChrome?.part}
                labelClassName={partChrome?.label}
                triggerClassName={partChrome?.control}
              />
            </div>
          ) : (
            <CountryField
              id={`${id}-country`}
              value={value.country}
              onChange={(val) => updateField('country', val)}
              className={partChrome?.part}
              labelClassName={partChrome?.label}
              triggerClassName={partChrome?.control}
            />
          )}
        </div>
      )}

      {error && (
        <p
          id={errorId}
          className={cn(
            'text-destructive',
            partChrome ? partChrome.hint : 'text-xs'
          )}
        >
          {error}
        </p>
      )}
    </div>
  );
}
